<template>
  <div v-if="$slots.default">
    <slot />
  </div>
  <div v-else>
    <v-row
      :class="[
        'mx-0 px-2 payment-single',
        {
          'v-card--disabled': payment.options.disabled,
          'active-reprogram': activeReprogram,
          'expired-payments': dueStatus < 0,
        }
      ]"
      @click="selectPaymentMethod"
    >
      <v-col class="d-flex align-center" cols="3">
        <v-checkbox
          class="mt-0 mr-3 pt-0"
          color="primary"
          :disabled="payment.options.disabled || seleccionDisabled"
          :input-value="payment.selected"
          hide-details
        ></v-checkbox>
        <div class="description">
          <span :class="[ 'description__title', { 'red--text': payment.status == 117 } ]" style="white-space: nowrap;"> {{ payment.title }}</span>
          <span class="subdescription" v-if="$isTeclab && dueDate">{{ `${$options.filters.$capitalize(dueDate.format('MMMM'))}` }}</span>
          <span class="subdescription" v-if="payment.options?.term?.classStartDate && payment.options?.term?.classEndDate">{{ $t('modules.portalalumno.payments.paymentsStudents.view.labels.complex.termSubtitle', { startMonth: getFormattedDate(payment.options.term.classStartDate, 'MMMM'), endMonth: getFormattedDate(payment.options.term.classEndDate, 'MMMM')}) }}</span>
          <span class="subdescription" v-else-if="payment.billingProduct == 87 && payment.subscription?.termCodes">{{ $t('modules.portalalumno.payments.paymentsStudents.view.subscription.term') }} {{ payment.subscription.termCodes }}</span>
          <span class="subdescription" v-if="payment.billingProduct == 87 && payment.subscription?.installments">{{ $t('modules.portalalumno.payments.paymentsStudents.view.subscription.info', { installments: payment.subscription.installments, mount: payment.subscription.amount }) }}</span>
          <span class="primary--text" v-if="isMandatory && $isMexico" style="font-size: 12px;">
            {{ $tc('modules.portalalumno.payments.paymentsStudents.view.reenrollment.mandatory', studentData.studentType.value == 7 ? 1 : 2) }}
          </span>
        </div>
      </v-col>

      <v-col class="due-info d-flex align-center" cols="3">
        <span v-if="payment.options?.term?.code || payment.options?.term?.description" class="d-flex align-center">
          <span class="mx-2">|</span>
          <strong style="white-space: nowrap">{{ $t('modules.portalalumno.payments.paymentsStudents.view.labels.complex.term', { term: payment.options?.term?.code?.replace('/', ' ') ||  payment.options?.term?.description?.replace('/', ' ')}) }}</strong>
        </span>
        <span v-if="dueDate && !isMandatory" class="d-flex align-center">
          <span class="mx-2">|</span>
          <span v-if="dueStatus < 0">{{ $t('modules.portalalumno.payments.paymentsStudents.view.labels.complex.expiredAt', { dueDate: dueDate.format('DD/MM/YY') }) }}</span>
          <span v-else :class="{ actualDueDate: dueStatus == 0 }">{{ $t('modules.portalalumno.payments.paymentsStudents.view.labels.complex.expiresAt', { dueDate: dueDate.format('DD/MM/YY') }) }}</span>
        </span>
      </v-col>

      <v-col class="d-flex justify-end pa-0 price-info" cols="6">
        <div v-if="hasRealDiscount || (!isMandatory && (activePrepaid || payment.options.discounts.length))" class="actual-prepaid d-flex flex-column align-end justify-center py-2 px-4" style="height: 100%;">
          <small v-if="activePrepaid" class="due-date">{{ `Hasta ${activePrepaid.dayTo}/${payment.options.month}` }}</small>
          <small v-if="hasRealDiscount" class="old-price">{{ payment.options.amount | $currency }}</small>
          <span class="discount-value">{{ `${realTotalDiscountPercentage}% OFF` }}</span>
          <span class="font-weight-medium text-no-wrap">{{ finalDiscountedAmount | $currency }}</span>
        </div>

        <div  v-if="!$isIPP" class="d-flex flex-column align-end justify-center pl-3 pr-1" style="height: 100%;">
          <small v-if="expiredDiscount > 0" class="old-price">{{ payment.options.amount | $currency }}</small>
          <small v-else-if="dueStatus >= 0" style="font-size: 11px;">{{ $t('modules.portalalumno.payments.paymentsStudents.view.labels.complex.listPrice') }}</small>
          <span class="font-weight-medium text-no-wrap">{{ payment.options.amount * (1 - expiredDiscount / 100) | $currency }}</span>
          <span v-if="expiredDiscount > 0" class="discount-value">{{ `${Math.round((expiredDiscount + Number.EPSILON) * 100) / 100}% OFF` }}</span>
        </div>
        <div v-if="$isIPP" class="d-flex flex-column align-end justify-center pl-6 pr-3"  style="font-size:22px; font-weight: 500">
          <small v-if="expiredDiscount >= 0" class="font-weight-medium" >{{ payment.options.amount | $currency }}</small>
          <small v-if="dueStatus >= 0" class="font-weight-regular description__title">{{ $t('modules.portalalumno.payments.paymentsStudents.view.labels.complex.listPrice') }}</small>
        </div>
      </v-col>

    </v-row>
</div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  props: {
    disabledPrepaid:{type: Boolean, default: false},
    payment: Object,
    systemConfig: Object,
    canReprogram: {type: Boolean, default: false},
    seleccionDisabled: {type: Boolean, default: false},
  },
  data() {
    return {
      dueDate: this.payment.options.dueDate ? moment(this.payment.options.dueDate) : null,
      activePrepaid: null,
    }
  },
  mounted() {
    if (this.disabledPrepaid) return
    this.setActivePrepaid()
  },
  computed: {
    isMandatory() {
      return this.payment.mandatory || this.payment.billingProduct == 87 || (this.$isIPP && this.payment.billingProduct == 89 && this.payment.options.installment == 1)
    },
    totalDiscountPercentage() {
      if (!this.payment.options.discounts || !this.payment.options.discounts.length) return 0
      return this.payment.options.discounts.reduce((total, discount) => total + (discount.percentage || 0), 0)
    },
    hasRealDiscount() {
      const { off = 0 } = this.payment.options;
      if (this.payment.billingProduct == 87) return false;
      return off > 0;
    },
    realTotalDiscountPercentage() {
      return this.payment.options.off || 0;
    },
    finalDiscountedAmount() {
      const { amount, off = 0 } = this.payment.options;
      return amount * (1 - off / 100);
    },
    expiredDiscount() {
      if ((this.isMandatory && this.activePrepaid) || (this.isMandatory && this.payment?.options?.off === 100)) return this.payment.options.off
      return this.duePrepaidPercentage
    },
    duePrepaidPercentage() {
      if (this.payment.billingProduct != this.$sisConstants('modules.products.cuota')) return 0
      let termId = this.payment.options ? this.payment.options.term?.id : null
      if (termId == 894 && this.payment.options.paymentInstallments == 6 && this.payment.options.paymentInstallment >= 4) termId = 696
      else if (termId == 960 && this.payment.options.paymentInstallments == 6 && this.payment.options.paymentInstallment >= 4) termId = 894

      if (!termId) return 0
      else if ([ 597, 630 ].includes(termId)) return 10.87  // 1A-2024 y 1B-2024
      else if ([ 663, 696 ].includes(termId)) return 8.37   // 2A-2024 y 2B-2024
      else if ([ 894, 960 ].includes(termId)) return 10.5   // 1A-2025 y 1B-2025
      else if ([ 1094, 1125 ].includes(termId)) return 8    // 2A-2025 y 2B-2025
      return 0
    },

    filteredPrepaids() {
      if (this.payment.options?.prepaids && this.payment.options?.prepaids.length) return this.payment.options?.prepaids
      return []
    },
    dueStatus() {
      if (this.payment.status == 117) return -1
      if (this.dueDate <= moment().endOf('month')) return 0
      return 1
    },
    isMaxPrepaidActive() {
      return (this.payment.options.paymentInstallment == 1 && this.payment.mandatory) ||
            (this.payment.options.paymentInstallment == 2 && this.payment.options.paymentInstallments == 5 && this.payment.billingProduct == 81) ||
            (this.payment.options.paymentInstallment == 1 && this.payment.options.inscriptionDate) ||
            //Validacion de mexico para mostrar pronto pago maximo en caso de que el ingreso sea post 16 del mes
            (this.payment.options.paymentInstallment == 2 && new Date(this.payment.options?.inscriptionDate)?.getUTCDate() >= 16 && this.$isMexico) ||
            (this.payment.billingProduct == 89 && this.payment.options.paymentInstallment == 1) ||
            (
              // Alumnos inscriptos al 1A/2024 entre el 12/10 y 31/10, se muestra pronto pago sin maximo descuento en duro
              this.$isIPP &&
              this.payment.options.paymentInstallment == 1 &&
              (this.payment.options.term?.id != 597 ||
              moment(this.payment.options.inscriptionDate) < moment('2023-10-12') ||
              moment(this.payment.options.inscriptionDate) > moment('2023-10-31'))
            ) || (
              this.$isIPP &&
              this.payment.options.inscriptionDate &&
              this.payment.options.term?.id == 498 &&
              [1, 2].includes(this.payment.options.paymentInstallment) &&
              moment(this.payment.options.inscriptionDate) >= moment('2023-08-08') &&
              moment(this.payment.options.inscriptionDate) <= moment('2023-08-24')
            )
    },
    activeReprogram() {
      return this.canReprogram && this.payment.selected && this.payment.repaymentAvailable
    },
    ...mapGetters({
      studentData: 'commons/getStudentData',
    }),
  },
  methods: {
    getFormattedDate(date, format = 'DD-MM-YYYY') {
      return moment(date).format(format)
    },
    setActivePrepaid() {
      if (!this.filteredPrepaids.length) return
      let activeFound = false

      // Sort filteredPrepaids by dayTo in ascending order
      const sortedPrepaids = [...this.filteredPrepaids].sort((a, b) => a.dayTo - b.dayTo);
      
      sortedPrepaids.forEach((prep, index) => {
        const dueDate = new Date(this.payment.options.dueDate);
        const expired = new Date() >= dueDate;

        const prepaidDate = moment(this.payment.options.dueDate).set('date', prep.dayTo + 1)
        const diff = prepaidDate.diff(this.now, 'seconds')
        if ((this.isMaxPrepaidActive && index == 0 && (!expired || this.payment.options.paymentInstallment == 1)) 
          || (diff > 0 && !activeFound && (!expired || this.payment.options.paymentInstallment == 1))) {
          this.activePrepaid = prep
          this.payment.options.off = prep.percentage ? prep.percentage : 0
          this.payment.options.prepaidIndex = index
          activeFound = true
        }
      })

      // Descuento excepcional aplicado a todo RI a partir del 1A/2024 ingresado previo al 2024
      if (
        this.$isIPP &&
        !activeFound &&
        moment(this.payment.options.inscriptionDate) < moment('2023-11-01') &&
        this.duePrepaidPercentage > 0
      ) {
        this.payment.options.off = this.duePrepaidPercentage
        this.payment.options.prepaidIndex = 'prepaidException'
      }
    },
    selectPaymentMethod() {
      if (!this.payment.options.disabled && !this.seleccionDisabled) {
        this.$emit('updatepaymentmethod', !this.payment.selected);
      }
    },
  }
};
</script>

<style scoped  lang="scss">

  .payment-single {
    height: 90%;
    border-top: solid 1px var(--v-primary-lighten5);
    &.expired-payments { border-top: solid 1px #FFDCDC; }

    &.active-reprogram { background-color: #FFF1F1; }
    &:hover { cursor: pointer; }
  }

  .description {
    display: flex;
    flex-direction: column;
    justify-content: space-around;

    .subdescription { font-size: 12px; }
  }
  .description__title { font-size: 15px; }

  .due-info {
    font-size: 12px;

    .actualDueDate {
      background-color: var(--v-primary-lighten5);
      border-radius: 4px;
      padding: 6px 8px;
    }
  }

  .price-info {
    .actual-prepaid {
      background-color: #EFF5FF;
      .due-date { line-height: 1.21; color: rgba(33, 33, 33, 0.5); }
    }
    .old-price {
      line-height: 1.21;
      color: rgba(33, 33, 33, 0.5);
      text-decoration: line-through;
    }

    .discount-value {
      line-height: 1.17;
      font-size: 14px;
      color: #2d9400;
    }

  }

</style>
